<template>
  <input
    v-if="!datalist"
    :id="id"
    v-model="modelValue"
    type="text"
    :name="id"
    :disabled="disabled"
    class="relative -mr-px block flex-1 overflow-hidden border border-border px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground first:rounded-l-md last:mr-0 last:rounded-r-md focus-within:z-10 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 dark:bg-input"
    :placeholder="placeholder"
  />
  <ComboboxRoot
    v-else
    v-model="modelValue"
    ignore-filter
    class="relative -mr-px block flex-1 overflow-hidden border border-border bg-background first:rounded-l-md last:mr-0 last:rounded-r-md focus-within:z-10 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 dark:bg-input dark:text-foreground"
  >
    <ComboboxAnchor>
      <ComboboxInput
        v-model="query"
        :placeholder="placeholder"
        class="w-full border-0 bg-transparent p-2 text-sm text-foreground focus:border-0 focus:ring-0 dark:text-muted-foreground"
      />
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        position="popper"
        side="bottom"
        align="start"
        class="z-20 max-h-60 select-none overflow-auto rounded-md border border-border bg-input text-left text-base shadow-lg focus:outline-none sm:text-sm dark:bg-background dark:text-foreground"
        style="width: var(--reka-combobox-trigger-width)"
      >
        <ComboboxViewport>
          <ComboboxEmpty
            class="relative cursor-default select-none px-4 py-2 text-muted-foreground"
          >
            Nothing found.
          </ComboboxEmpty>

          <!-- <ComboboxItem> doesn't like it if we change the `value` dynamically, so it's keyed -->
          <ComboboxItem
            v-if="query && isNonexistent"
            :key="query"
            :value="query"
            class="px-3 py-2 text-foreground data-[highlighted]:bg-blue-600 data-[highlighted]:text-white"
          >
            <span>Create "{{ query }}"</span>
          </ComboboxItem>

          <ComboboxItem
            v-for="key in filteredKeys"
            :key="key"
            :value="key"
            class="px-3 py-2 text-foreground data-[highlighted]:bg-blue-600 data-[highlighted]:text-white"
          >
            <span>{{ key }}</span>
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<script lang="ts" setup>
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxViewport,
} from "reka-ui";
import { computed, ref } from "vue";

const props = defineProps<{
  id: string;
  datalist?: Array<string>;
  placeholder?: string;
  disabled?: boolean;
}>();

const modelValue = defineModel<string>("modelValue", { default: "" });

const query = ref("");
const normalizedQuery = computed(() => query.value.toLowerCase().trim());

const filteredKeys = computed(() => {
  const datalist = props.datalist;
  if (!datalist) {
    return [];
  }

  const term = normalizedQuery.value;
  if (!term) {
    return datalist || [];
  }

  return datalist.filter((key) => key.toLowerCase().includes(term));
});

const isNonexistent = computed(() => {
  return !filteredKeys.value.includes(query.value);
});
</script>
